import _escape from "lodash/escape";
import React from "react";
import { useRothko } from "web/react/hooks/use-rothko/use-rothko";
import analytics from "web/script/analytics/analytics";
import globals from "web/script/modules/globals";
import userProfiler from "web/script/modules/userprofiler";
import { getSearchPageURL } from "web/script/routing/routes";
import navigate from "web/script/utils/navigate";
import { AutoCompleteSerializer, ProfileSuggestion, QuerySuggestion } from "web/types/serializers";

const AUTO_COMPLETE_API_ENDPOINT = "modules/search_input/auto_complete";
const EVENT_CATEGORY = "nav_search";

interface AutoCompleteContextProps {
    searchInputRef: React.RefObject<HTMLInputElement> | null;
    activeSuggestionIndex: number | null;
    query: string;
    hasAnySuggestions: boolean;
    querySuggestions: QuerySuggestion[];
    profileSuggestions: ProfileSuggestion[];
    updateAutocomplete: (any) => void;
    makeSuggestionActive: (index: number | null, replace?: boolean) => void;
    onKeyDown: (event: React.SyntheticEvent) => void;
    onSubmit: (event: React.SyntheticEvent) => void;
    onInput: (event: React.SyntheticEvent) => void;
    onSuggestionClick: (even: React.SyntheticEvent) => void;
    isVisible: boolean;
}

export const AutoCompleteContext = React.createContext<AutoCompleteContextProps>({
    searchInputRef: null,
    activeSuggestionIndex: null,
    query: "",
    hasAnySuggestions: false,
    querySuggestions: [],
    profileSuggestions: [],
    updateAutocomplete: () => {},
    makeSuggestionActive: () => {},
    onKeyDown: () => {},
    onSubmit: () => {},
    onInput: () => {},
    onSuggestionClick: () => {},
    isVisible: false,
});

function AutoCompleteProvider({ children }: ReactComponentProps): React.ReactElement {
    const searchInputRef = React.useRef<HTMLInputElement>(null);
    const [activeSuggestionIndex, setActiveSuggestionIndex] = React.useState<number | null>(null);
    const [activeQuery, setActiveQuery] = React.useState<string | null>(null);
    const [useActiveQuery, setUseActiveQuery] = React.useState<boolean>(false);
    const [hasAnySuggestions, setHasAnySuggestions] = React.useState<boolean>(false);
    const [querySuggestions, setQuerySuggestions] = React.useState<QuerySuggestion[]>([]);
    const [profileSuggestions, setProfileSuggestions] = React.useState<ProfileSuggestion[]>([]);
    const [suggestions, setSuggestions] = React.useState<(QuerySuggestion | ProfileSuggestion)[]>(
        []
    );
    const [query, setQuery] = React.useState<string>("");
    const [gender, setGender] = React.useState<string>("Women");
    const [onlyBrand, setOnlyBrand] = React.useState<boolean>(false);
    const returnedQuery = useActiveQuery ? activeQuery || "" : query;
    const isVisible = hasAnySuggestions && returnedQuery !== "";

    const { data, run } = useRothko<AutoCompleteSerializer>(
        AUTO_COMPLETE_API_ENDPOINT,
        {
            query,
            gender,
        },
        {
            enabled: false,
        }
    );

    React.useEffect(() => {
        /**
         * If query is falsey then the suggestions
         * list is populated with the users previous search terms. Otherwise the components data is
         * fetched using Rothko.
         */
        if (!query && !onlyBrand) {
            const previousSearchTerms = userProfiler.getSearchTerms();
            const hasPreviousSearchTerms = !!previousSearchTerms;

            if (hasPreviousSearchTerms) {
                setHasAnySuggestions(hasPreviousSearchTerms);
                setQuerySuggestions(
                    previousSearchTerms
                        .slice(0, 5)
                        .map((term) => ({ query: _escape(term), type: "query" }))
                );
            }
        } else {
            run();
        }
    }, [gender, query, onlyBrand, run]);

    React.useEffect(() => {
        if (data) {
            let profileSuggestionsData = data.profile_suggestions;
            let querySuggestionsData = data.query_suggestions;

            if (onlyBrand) {
                querySuggestionsData = [];
                profileSuggestionsData = data.profile_suggestions.filter(
                    (suggestion) => suggestion.profile_type === "Brand"
                );
            }

            setQuerySuggestions(querySuggestionsData);
            setProfileSuggestions(profileSuggestionsData);
            setSuggestions([...querySuggestionsData, ...profileSuggestionsData]);
            setHasAnySuggestions(data.has_any_suggestions);
        }
    }, [data, onlyBrand]);

    function makeSuggestionActive(globalIndex: number | null, replaceQuery = false): void {
        setActiveSuggestionIndex(globalIndex !== activeSuggestionIndex ? globalIndex : null);

        if (replaceQuery) {
            const newQuery =
                globalIndex !== null
                    ? globalIndex >= querySuggestions.length
                        ? query
                        : querySuggestions[globalIndex].query
                    : query;
            setActiveQuery(newQuery);
            setUseActiveQuery(true);
        }
    }

    function navigateSuggestions(direction: "next" | "prev", replaceQuery = false): void {
        const step = direction === "next" ? 1 : -1;
        const suggestionsLength = querySuggestions.length + profileSuggestions.length;

        if (activeSuggestionIndex === null) {
            makeSuggestionActive(step === 1 ? 0 : suggestionsLength + step, replaceQuery);
            return;
        }

        if (
            (activeSuggestionIndex === 0 && step === -1) ||
            (activeSuggestionIndex === suggestionsLength - 1 && step === 1)
        ) {
            makeSuggestionActive(null, replaceQuery);
            return;
        }

        makeSuggestionActive(activeSuggestionIndex + step, replaceQuery);
    }

    function updateAutocomplete({ gender, query, onlyBrand = false }): void {
        if (gender !== undefined) {
            setGender(gender);
        }

        if (query !== undefined) {
            setQuery(query);
        }

        setOnlyBrand(onlyBrand);
    }

    function onKeyDown(event): void {
        switch (event.key) {
            // up key
            case "ArrowUp":
                event.preventDefault();
                navigateSuggestions("prev", true);
                break;

            // down key
            case "ArrowDown":
                event.preventDefault();
                navigateSuggestions("next", true);
                break;

            // tab key
            case "Tab":
                event.preventDefault();
                navigateSuggestions(event.shiftKey === true ? "prev" : "next", true);
                break;
        }
    }

    function onInput(event): void {
        setUseActiveQuery(false);
        setQuery(event.target.value);
    }

    function getAnalyticsEventAction(
        action: string,
        suggestion?: QuerySuggestion | ProfileSuggestion
    ): string {
        const searchQuery = useActiveQuery ? activeQuery?.trim() || "" : query.trim();
        let type;

        if (searchQuery === "") {
            type = "previous_search_term";
        } else {
            type = `${suggestion?.type}_suggestion`;
        }

        return action + "_on_" + type;
    }

    function submitSearch(suggestion: QuerySuggestion): void {
        searchInputRef?.current?.blur();

        let target = new URL(getSearchPageURL(), globals.window.location.href);
        const searchQuery = suggestion
            ? suggestion.query
            : useActiveQuery
            ? activeQuery?.trim() || ""
            : query.trim();

        target.searchParams.set("q", searchQuery);
        target.searchParams.set("term", searchQuery);
        target.searchParams.set("gender", gender);

        navigate(target.href);
    }

    function onSubmit(event): void {
        event.preventDefault();

        if (query.length === 0) {
            return;
        }

        const activeSuggestion = activeSuggestionIndex
            ? suggestions[activeSuggestionIndex]
            : undefined;

        const eventAction = getAnalyticsEventAction("enter", activeSuggestion);

        if (activeSuggestion && ["category", "profile"].includes(activeSuggestion.type)) {
            analytics.event(EVENT_CATEGORY, eventAction).then(function () {
                navigate((activeSuggestion as ProfileSuggestion).path);
            });
            return;
        } else if (activeSuggestion) {
            analytics.event(EVENT_CATEGORY, eventAction, activeSuggestionIndex?.toString());
        } else {
            let label = "without_suggestions";

            if (hasAnySuggestions) {
                label = "with_suggestions";
            }

            analytics.event(EVENT_CATEGORY, "submit_search_input", label);
        }

        submitSearch(activeSuggestion as QuerySuggestion);
    }

    function onSuggestionClick(event): void {
        event.preventDefault();
        const suggestionIndex = parseInt(event.currentTarget.dataset.suggestionGlobalIndex, 0);
        makeSuggestionActive(suggestionIndex, true);
        const activeSuggestion = suggestions[suggestionIndex];
        const analyticsAction = getAnalyticsEventAction("click", activeSuggestion);

        if (activeSuggestion.type === "query") {
            analytics
                .event(EVENT_CATEGORY, analyticsAction, suggestionIndex.toString())
                .then(() => {
                    submitSearch(activeSuggestion as QuerySuggestion);
                });
            return;
        } else if (["category", "profile"].includes(activeSuggestion.type || "")) {
            analytics.event(EVENT_CATEGORY, analyticsAction).then(function () {
                navigate((activeSuggestion as ProfileSuggestion).path);
            });
        }
    }

    return (
        <AutoCompleteContext.Provider
            value={{
                searchInputRef,
                activeSuggestionIndex,
                query: returnedQuery,
                makeSuggestionActive,
                hasAnySuggestions,
                querySuggestions,
                profileSuggestions,
                updateAutocomplete,
                onKeyDown,
                onInput,
                onSubmit,
                onSuggestionClick,
                isVisible,
            }}
        >
            {children}
        </AutoCompleteContext.Provider>
    );
}

export default AutoCompleteProvider;
